import React, { FC, useState } from 'react';
import {
	createSearchParams,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
// import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
// import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
// import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
// import CodeIcon from '@mui/icons-material/Code';
// import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { Typography, useTheme } from '@mui/material';
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
// import LoupeIcon from '@mui/icons-material/Loupe';
import {
	COURSE_FILTERS,
	COURSE_STATUSES,
} from '../../lib/helpers/courses.helper';
import PATHS from '../../lib/consts/paths';
import { ICourseOccupation } from '../../lib/interfaces/CourseOccupation';
import {
	CONTENT_FIELDS,
	getContentFieldValue,
} from '../../lib/helpers/content.helper';
import { setLastCourseParams } from '../../redux/reducers/ui/urlReducer';

type CoursesSidebarProps = {
	courseOccupations: ICourseOccupation[];
};

type FilterListItem = {
	title: string;
	value: string;
	icon: string | React.ReactElement;
	onClick: () => void;
};

const FilterSubtitle: FC<{ text: string }> = ({ text }) => {
	const CWtheme = useTheme();
	const { t } = useTranslation();
	return (
		<Typography
			variant="h6"
			component="h6"
			sx={{
				padding: '15px 14px 0 18px',
				color: CWtheme.palette.primary.light,
				fontSize: '0.825rem',
				fontWeight: 600,
			}}
		>
			<span style={{ paddingRight: '15px' }}>{t(text)}</span>
		</Typography>
	);
};

const FilterListItemRenderer: FC<{ item: FilterListItem }> = ({ item }) => {
	const CWtheme = useTheme();
	const { t } = useTranslation();
	return (
		<ListItem key={item.value} disablePadding onClick={item.onClick}>
			<ListItemButton>
				<ListItemIcon
					sx={{ color: CWtheme.palette.background.paper, minWidth: '35px' }}
				>
					{item.icon}
				</ListItemIcon>
				<ListItemText primary={t(item.title)} />
			</ListItemButton>
		</ListItem>
	);
};

const CoursesSidebar: FC<CoursesSidebarProps> = ({ courseOccupations }) => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [urlParams, setUrlParams] = useState<any>(null);
	const [searchParams] = useSearchParams();
	const way: string = searchParams.get(COURSE_FILTERS.WAY) || '';
	const status: string | null = searchParams.get(COURSE_FILTERS.STATUS);
	const currentLanguage: string = i18n.language;

	const addToUrlParams = (type: string, value: string | null) => {
		const params = urlParams ? { ...urlParams } : {};
		if (!value) {
			delete params[type];
		} else {
			params[type] = value;
		}
		setUrlParams(value === 'all' ? {} : params);
		dispatch(setLastCourseParams(params));

		if (value === 'all') {
			navigate(PATHS.courses);
		} else {
			navigate({
				pathname: PATHS.courses,
				search: `${createSearchParams({
					...(params.way && { way: params.way }),
					...(params.status && { status: params.status }),
				})}`,
			});
		}
	};

	const selectedIcon = (
		<RadioButtonCheckedIcon color="secondary" fontSize="small" />
	);

	const occupations: FilterListItem[] = courseOccupations.map((occupation) => {
		const name = getContentFieldValue({
			entityContent: occupation.content,
			field: CONTENT_FIELDS.NAME,
			language: currentLanguage,
		});
		return {
			title: name,
			value: occupation.slug,
			icon: occupation.slug === way ? selectedIcon : '',
			onClick: () => addToUrlParams(COURSE_FILTERS.WAY, occupation.slug),
		};
	});

	const courseStatuses: FilterListItem[] = [
		{
			title: t('all'),
			value: COURSE_STATUSES.ALL,
			icon: !status ? selectedIcon : '',
			onClick: () => addToUrlParams(COURSE_FILTERS.STATUS, null),
		},
		{
			title: 'New',
			value: COURSE_STATUSES.NEW,
			icon: COURSE_STATUSES.NEW === status ? selectedIcon : '',
			onClick: () => addToUrlParams(COURSE_FILTERS.STATUS, COURSE_STATUSES.NEW),
		},
		{
			title: t('inProgress'),
			value: COURSE_STATUSES.IN_PROGRESS,
			icon: COURSE_STATUSES.IN_PROGRESS === status ? selectedIcon : '',
			onClick: () =>
				addToUrlParams(COURSE_FILTERS.STATUS, COURSE_STATUSES.IN_PROGRESS),
		},
		{
			title: 'Completed',
			value: COURSE_STATUSES.COMPLETED,
			icon: COURSE_STATUSES.COMPLETED === status ? selectedIcon : '',
			onClick: () =>
				addToUrlParams(COURSE_FILTERS.STATUS, COURSE_STATUSES.COMPLETED),
		},
	];
	/* const courseTypes = [
		{ title: 'Theory', value: 'theory', icon: <SchoolIcon /> },
		{ title: 'Practice', value: 'practice', icon: <SportsMartialArtsIcon /> },
	]; */

	return (
		<Box sx={{ overflow: 'auto', paddingTop: 2 }}>
			<List sx={{ paddingTop: 0, paddingLeft: '10px' }}>
				<FilterSubtitle text="category" />
				{occupations.map((item) => (
					<FilterListItemRenderer item={item} />
				))}
			</List>
			{/* <Divider /> */}
			<List sx={{ paddingTop: 0, paddingLeft: '10px' }}>
				<FilterSubtitle text="status" />
				{courseStatuses.map((item) => (
					<FilterListItemRenderer item={item} />
				))}
			</List>
		</Box>
	);
};

export default CoursesSidebar;
